import i18n from "i18next";
import { initReactI18next } from "react-i18next";

// `preview: true` marks a locale that ships in the resources bundle and
// resolves correctly via i18next + browser detection + saved preference,
// but is hidden from the in-app `LanguageSelector` dropdown until a
// native-speaker review pass is complete. Set
//   VITE_JINGLE_SHOW_PREVIEW_LOCALES=<code>
// (comma-separated) to opt a preview entry back into the selector for a
// staging review build. Existing saved sessions are NEVER broken — i18n
// still loads the bundle and `setLocale(code)` still works
// programmatically; only the visible dropdown option is gated.
//
// pa-Guru graduated from preview after Task #272 — all `_TODO_pa`
// placeholders were resolved and a bilingual native-speaker QA pass
// signed off the ~3,100-line locale as natural and age-appropriate for
// a family Christmas app.
export const SUPPORTED_LOCALES = [
  { code: "en", nativeName: "English" },
  { code: "fr-CA", nativeName: "Français canadien" },
  { code: "es", nativeName: "Español" },
  { code: "pa-Guru", nativeName: "ਪੰਜਾਬੀ" },
] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]["code"];

// Locales the `LanguageSelector` should render in its dropdown. Filters
// out any entry flagged `preview: true` unless its code appears in
// VITE_JINGLE_SHOW_PREVIEW_LOCALES (comma-separated).
export function visibleLocales(): readonly { code: SupportedLocale; nativeName: string }[] {
  const raw = (import.meta.env.VITE_JINGLE_SHOW_PREVIEW_LOCALES as string | undefined) ?? "";
  const allowed = new Set(
    raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean),
  );
  return SUPPORTED_LOCALES.filter(
    (l) => !("preview" in l && l.preview) || allowed.has(l.code),
  );
}

export const DEFAULT_LOCALE: SupportedLocale = "en";

const STORAGE_KEY = "jingle.lang";

// Map a raw browser locale (e.g. "fr-FR", "es-MX", "pa-IN") to one of the
// app's supported codes. Returns null when no plausible match exists, in
// which case i18next falls back to English.
export function resolveBrowserLocale(input: string | undefined): SupportedLocale | null {
  if (!input) return null;
  const lower = input.toLowerCase();
  if (lower === "en" || lower.startsWith("en-")) return "en";
  if (lower === "fr-ca") return "fr-CA";
  if (lower === "fr" || lower.startsWith("fr-")) return "fr-CA";
  if (lower === "es" || lower.startsWith("es-")) return "es";
  if (lower === "pa-guru" || lower === "pa") return "pa-Guru";
  if (lower.startsWith("pa-")) return "pa-Guru";
  return null;
}

// IMPORTANT: do NOT enable `nonExplicitSupportedLngs`. Our supported codes
// include hyphenated regions (`fr-CA`, `pa-Guru`) but no bare bases
// (`fr`, `pa`). With `nonExplicitSupportedLngs:true`, i18next would happily
// resolve `fr-CA` → `fr`, fail to find resources for `fr`, and silently
// fall back to English — which is exactly the bug we hit during the
// Phase 1.5.1 smoke test. Browser-locale normalization (mapping any `fr-*`
// to `fr-CA`, etc.) is done explicitly in `resolveBrowserLocale` and wired
// through `detectInitialLocale`, so we never need i18next's own
// non-explicit fallback.

// Map our supported codes to the right BCP-47 value for `<html lang>`.
// Browsers/screen-readers/translators key off this attribute, and the
// SEO routes already publish a `lang` attribute via the SeoTopicPage —
// the marketing site needs to match it whenever the user toggles
// languages. We keep the table next to the locale list so adding a new
// locale is one place, not two.
function htmlLangFor(locale: SupportedLocale): string {
  switch (locale) {
    case "en":
      return "en";
    case "fr-CA":
      return "fr-CA";
    case "es":
      return "es";
    case "pa-Guru":
      return "pa";
  }
}

function syncHtmlLang(locale: SupportedLocale): void {
  if (typeof document === "undefined") return;
  try {
    document.documentElement.lang = htmlLangFor(locale);
  } catch {
    /* document may be unavailable in tests / SSR */
  }
}

// Detect the initial locale before i18n init. Checks, in order:
//   1. URL ?lng= querystring — highest priority so deep-links and the
//      marketing-screens capture harness can force a locale without
//      depending on browser settings or persisted state.
//   2. localStorage (STORAGE_KEY) — the user's previous explicit choice.
//   3. navigator.language / navigator.languages[0] — browser preference,
//      normalised through resolveBrowserLocale.
//   4. DEFAULT_LOCALE ("en") — final safe fallback.
function detectInitialLocale(): SupportedLocale {
  if (typeof window === "undefined") return DEFAULT_LOCALE;

  // 1. Querystring ?lng=
  try {
    const params = new URLSearchParams(window.location.search);
    const qLng = params.get("lng");
    if (qLng) {
      const found = SUPPORTED_LOCALES.find((l) => l.code === qLng);
      if (found) return found.code;
    }
  } catch {
    /* URLSearchParams unavailable — skip */
  }

  // 2. localStorage
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const found = SUPPORTED_LOCALES.find((l) => l.code === saved);
      if (found) return found.code;
    }
  } catch {
    /* localStorage may be blocked (private browsing, iframe sandbox) */
  }

  // 3. Browser navigator
  const browserLang =
    (typeof navigator !== "undefined" &&
      (navigator.language || navigator.languages?.[0])) ||
    undefined;
  const resolved = resolveBrowserLocale(browserLang);
  if (resolved) return resolved;

  return DEFAULT_LOCALE;
}

// Load the translation bundle for a given locale on demand. Each branch
// uses a static string literal so Vite can analyse all four import() paths
// at build time and emit per-locale chunks (i18n-locale-*) — but those
// chunks are NOT added to the initial <link rel="modulepreload"> graph.
// Only the active user locale is fetched on first paint; the other three
// are never fetched unless the user explicitly switches language.
async function loadLocaleTranslations(
  code: SupportedLocale,
): Promise<Record<string, unknown>> {
  switch (code) {
    case "fr-CA":
      return (await import("./locales/fr-CA.json")).default as Record<string, unknown>;
    case "es":
      return (await import("./locales/es.json")).default as Record<string, unknown>;
    case "pa-Guru":
      return (await import("./locales/pa-Guru.json")).default as Record<string, unknown>;
    default:
      return (await import("./locales/en.json")).default as Record<string, unknown>;
  }
}

// Ensure a locale's resource bundle is resident in i18next. No-ops if
// the bundle is already loaded — safe to call on every language switch.
async function ensureLocaleLoaded(code: SupportedLocale): Promise<void> {
  if (i18n.hasResourceBundle(code, "translation")) return;
  const translations = await loadLocaleTranslations(code);
  i18n.addResourceBundle(code, "translation", translations, true, true);
}

// Initialises i18next with the active locale + English fallback resources.
// Exported so main.tsx can await it before first render — prevents any
// flash of untranslated content on first paint without shipping all four
// locale bundles to every visitor.
//
//   English user first visit:  en.json only fetched      (~118 kB raw)
//   French  user first visit:  en.json + fr-CA.json      (~243 kB raw, parallel)
//   Punjabi user first visit:  en.json + pa-Guru.json    (~364 kB raw, parallel)
//
// English is always loaded so `fallbackLng: "en"` can actually resolve
// missing nested keys in non-English locales (the non-English files have
// intentional coverage gaps filled by fallbackLng at runtime — lint-locales
// checks only that non-English files contain no orphaned keys). Other locales are
// fetched lazily when setLocale() is called. The per-locale Vite chunks
// cache independently of app-code changes.
export const i18nReadyPromise: Promise<void> = (async () => {
  const locale = detectInitialLocale();

  // Always fetch English (needed for fallbackLng); fetch the active locale
  // in parallel when it differs. Each fetch is individually guarded: if a
  // chunk fails to load (offline, CDN error, corrupt asset) we degrade
  // gracefully — English falls back to an empty object so keys are shown
  // verbatim rather than crashing, and a failing active-locale chunk simply
  // means the user sees English until they refresh with connectivity.
  const [enTranslations, localeTranslations] = await Promise.all([
    loadLocaleTranslations(DEFAULT_LOCALE).catch((): Record<string, unknown> => ({})),
    locale !== DEFAULT_LOCALE
      ? loadLocaleTranslations(locale).catch((): undefined => undefined)
      : undefined,
  ]);

  const resources: Record<string, { translation: Record<string, unknown> }> = {
    [DEFAULT_LOCALE]: { translation: enTranslations },
  };
  if (localeTranslations) {
    resources[locale] = { translation: localeTranslations };
  }

  await i18n.use(initReactI18next).init({
    lng: locale,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: SUPPORTED_LOCALES.map((l) => l.code),
    load: "currentOnly",
    resources,
    interpolation: { escapeValue: false },
    returnEmptyString: false,
    returnNull: false,
  });

  syncHtmlLang(locale);
})();

// Re-sync <html lang> on every language change so setLocale() AND any
// other path into i18n.changeLanguage() keep the attribute in lockstep
// with the React tree.
i18n.on("languageChanged", (lng) => {
  const safe = (SUPPORTED_LOCALES.find((l) => l.code === lng)?.code ??
    DEFAULT_LOCALE) as SupportedLocale;
  syncHtmlLang(safe);
});

export function setLocale(locale: SupportedLocale): void {
  // Defensive: only ever switch to a code that's actually in our
  // SUPPORTED_LOCALES list. Bad input (corrupted storage, future-typed
  // callers) silently falls back to English instead of breaking the UI.
  const safe = (SUPPORTED_LOCALES.find((l) => l.code === locale)?.code ??
    DEFAULT_LOCALE) as SupportedLocale;

  // Persist the choice immediately so page reloads pick up the locale
  // even before the async bundle fetch completes.
  try {
    window.localStorage.setItem(STORAGE_KEY, safe);
  } catch {
    /* localStorage may be unavailable */
  }
  // Sync html-lang eagerly so callers reading the attribute synchronously
  // (testing helpers) see the new value immediately.
  syncHtmlLang(safe);

  // Load the bundle BEFORE calling changeLanguage so the UI switches
  // fully translated — no flash of English while the JSON is in flight.
  // changeLanguage fires "languageChanged" → syncHtmlLang (second call
  // is a harmless no-op since the lang is already correct).
  //
  // On load failure: revert the persisted choice and html lang so the
  // app stays coherent — the user continues in `previous` rather than
  // ending up with a persisted preference for a locale whose bundle is
  // not resident.
  const previous = currentLocale();
  void ensureLocaleLoaded(safe)
    .then(() => {
      void i18n.changeLanguage(safe);
    })
    .catch(() => {
      try {
        window.localStorage.setItem(STORAGE_KEY, previous);
      } catch {
        /* ignore */
      }
      syncHtmlLang(previous);
    });
}

export function currentLocale(): SupportedLocale {
  const raw = i18n.resolvedLanguage ?? i18n.language ?? DEFAULT_LOCALE;
  const found = SUPPORTED_LOCALES.find((l) => l.code === raw);
  return (found?.code ?? DEFAULT_LOCALE) as SupportedLocale;
}

export default i18n;
